import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, FileText, Plus, Trash2, ExternalLink,
  Eye, EyeOff, ArrowUp, ArrowDown, Save, Loader2, RefreshCw,
} from 'lucide-react';
import { cmsOwner } from '../../lib/cmsApi';
import { BLOCK_TYPES } from '../../components/site/blocks';
import { toast } from 'sonner';

const STATUS_STYLES = {
  published: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  draft: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  archived: 'orka-surface-subtle orka-text-secondary border orka-border-subtle',
};

export default function OwnerWebsitePage() {
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [pageDetail, setPageDetail] = useState(null);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const refreshPages = async () => {
    setLoadingPages(true);
    try {
      const d = await cmsOwner.listPages();
      setPages(d.items || []);
      if (!selectedSlug && d.items?.length) setSelectedSlug(d.items[0].page_slug);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingPages(false);
    }
  };

  const refreshDetail = async (slug) => {
    if (!slug) return;
    setLoadingDetail(true);
    try {
      const d = await cmsOwner.getPage(slug);
      setPageDetail(d);
    } catch (e) {
      toast.error(`Failed to load page: ${e.message}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => { refreshPages(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedSlug) refreshDetail(selectedSlug); /* eslint-disable-next-line */ }, [selectedSlug]);

  const onTogglePublish = async (page) => {
    const next = page.status === 'published' ? 'draft' : 'published';
    try {
      await cmsOwner.setPageStatus(page.page_slug, next);
      toast.success(`Page ${next}`);
      refreshPages();
      if (page.page_slug === selectedSlug) refreshDetail(page.page_slug);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onSeed = async () => {
    setSeeding(true);
    try {
      const r = await cmsOwner.seed();
      toast.success(`Seed complete · pages added: ${r.pages_seeded}, blocks added: ${r.blocks_seeded}`);
      refreshPages();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const onAddBlock = async (block_type) => {
    if (!pageDetail?.page) return;
    const order = (pageDetail.blocks?.length || 0);
    try {
      await cmsOwner.createBlock({ page_slug: pageDetail.page.page_slug, block_type, data: {}, order });
      toast.success(`Block added · ${block_type}`);
      refreshDetail(selectedSlug);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="owner-cms-page">
      <Header onSeed={onSeed} seeding={seeding} onRefresh={refreshPages} />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 lg:col-span-3">
          <PageList
            pages={pages}
            loading={loadingPages}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
            onTogglePublish={onTogglePublish}
            error={error}
          />
        </div>
        <div className="col-span-12 md:col-span-8 lg:col-span-9">
          {pageDetail ? (
            <PageDetail
              detail={pageDetail}
              loading={loadingDetail}
              onAddBlock={onAddBlock}
              onChange={() => refreshDetail(selectedSlug)}
              onTogglePublish={onTogglePublish}
            />
          ) : (
            <div className="rounded-2xl border orka-border-subtle p-8 text-center orka-text-tertiary text-sm" data-testid="owner-cms-no-page">
              Select a page to edit blocks.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Header({ onSeed, seeding, onRefresh }) {
  return (
    <div className="flex items-center justify-between" data-testid="owner-cms-header">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl orka-surface-raised border orka-border-subtle flex items-center justify-center">
          <Globe className="w-5 h-5 orka-text-intent" />
        </div>
        <div>
          <h2 className="text-lg font-semibold orka-text-primary">Website CMS</h2>
          <p className="text-xs orka-text-tertiary">Authoring for the public marketing site at <code className="font-mono">/site/*</code></p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href="/site/home"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border orka-border-default orka-text-primary hover:orka-surface-subtle orka-transition-colors"
          data-testid="owner-cms-view-site"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View site
        </a>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border orka-border-default orka-text-primary hover:orka-surface-subtle orka-transition-colors"
          data-testid="owner-cms-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          type="button"
          onClick={onSeed}
          disabled={seeding}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 disabled:opacity-50 orka-transition-colors"
          data-testid="owner-cms-seed"
        >
          {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {seeding ? 'Seeding…' : 'Seed pages'}
        </button>
      </div>
    </div>
  );
}

function PageList({ pages, loading, selectedSlug, onSelect, onTogglePublish, error }) {
  const grouped = useMemo(() => {
    const top = pages.filter(p => !p.parent_slug);
    const byParent = pages.reduce((acc, p) => {
      if (p.parent_slug) {
        (acc[p.parent_slug] ||= []).push(p);
      }
      return acc;
    }, {});
    return { top, byParent };
  }, [pages]);

  if (loading) {
    return <div className="rounded-2xl border orka-border-subtle p-4 space-y-2 animate-pulse" data-testid="owner-cms-pagelist-loading">
      {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-7 orka-surface-subtle rounded-md" />)}
    </div>;
  }
  if (error) {
    return <div className="rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-950/30 p-4 text-rose-700 text-sm" data-testid="owner-cms-pagelist-error">{error}</div>;
  }

  return (
    <div className="rounded-2xl border orka-border-subtle orka-surface-base p-2 max-h-[calc(100vh-14rem)] overflow-auto" data-testid="owner-cms-pagelist">
      {grouped.top.map(p => (
        <PageRow
          key={p.page_slug}
          page={p}
          selected={p.page_slug === selectedSlug}
          onSelect={onSelect}
          onTogglePublish={onTogglePublish}
          children={grouped.byParent[p.page_slug] || []}
          selectedSlug={selectedSlug}
        />
      ))}
      {grouped.top.length === 0 && (
        <div className="p-6 text-center orka-text-tertiary text-sm">No pages yet. Click <strong>Seed pages</strong>.</div>
      )}
    </div>
  );
}

function PageRow({ page, selected, onSelect, onTogglePublish, children = [], selectedSlug }) {
  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(page.page_slug)}
        className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 orka-transition-colors ${
          selected ? 'orka-surface-selected' : 'hover:orka-surface-subtle'
        }`}
        data-testid={`owner-cms-page-${page.page_slug.replace(/\//g, '-')}`}
      >
        <FileText className="w-4 h-4 orka-text-tertiary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm orka-text-primary truncate">{page.title}</div>
          <div className="text-[11px] orka-text-tertiary font-mono truncate">/{page.page_slug}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-semibold ${STATUS_STYLES[page.status]}`}>
          {page.status}
        </span>
        {typeof page.block_count === 'number' && (
          <span className="text-[10px] font-mono orka-text-tertiary">{page.block_count}b</span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePublish(page); }}
          title={page.status === 'published' ? 'Unpublish' : 'Publish'}
          className="w-7 h-7 rounded-md inline-flex items-center justify-center orka-text-tertiary hover:orka-text-primary"
          data-testid={`owner-cms-page-toggle-${page.page_slug.replace(/\//g, '-')}`}
        >
          {page.status === 'published' ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </button>
      {children.length > 0 && (
        <div className="ml-5 border-l orka-border-subtle">
          {children.map(c => (
            <button
              key={c.page_slug}
              type="button"
              onClick={() => onSelect(c.page_slug)}
              className={`w-full text-left px-3 py-1.5 rounded-md flex items-center gap-2 orka-transition-colors ${
                c.page_slug === selectedSlug ? 'orka-surface-selected' : 'hover:orka-surface-subtle'
              }`}
              data-testid={`owner-cms-page-${c.page_slug.replace(/\//g, '-')}`}
            >
              <span className="text-xs orka-text-secondary truncate">{c.title}</span>
              <span className={`ml-auto text-[10px] uppercase px-1 py-0.5 rounded-sm border ${STATUS_STYLES[c.status]}`}>{c.status}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function PageDetail({ detail, loading, onAddBlock, onChange, onTogglePublish }) {
  const { page, blocks } = detail;
  if (loading) {
    return <div className="rounded-2xl border orka-border-subtle p-8 animate-pulse h-64 orka-surface-subtle" data-testid="owner-cms-detail-loading" />;
  }
  return (
    <div className="space-y-3" data-testid="owner-cms-detail">
      <div className="rounded-2xl border orka-border-subtle orka-surface-base p-4 flex items-center justify-between">
        <div>
          <div className="text-xs orka-text-tertiary font-mono">/site/{page.page_slug}</div>
          <h3 className="text-base font-semibold orka-text-primary mt-0.5">{page.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/site/${page.page_slug}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border orka-border-default text-xs orka-text-primary hover:orka-surface-subtle"
            data-testid="owner-cms-detail-preview">
            <ExternalLink className="w-3 h-3" /> Preview
          </a>
          <button
            type="button"
            onClick={() => onTogglePublish(page)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium ${
              page.status === 'published' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'
            }`}
            data-testid="owner-cms-detail-publish-toggle"
          >
            {page.status === 'published' ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {page.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>

      <div className="space-y-3" data-testid="owner-cms-blocks">
        {blocks.map((b, i) => (
          <BlockEditor
            key={b.block_id}
            block={b}
            index={i}
            total={blocks.length}
            onChange={onChange}
          />
        ))}
        {blocks.length === 0 && (
          <div className="rounded-2xl border orka-border-subtle p-6 text-center orka-text-tertiary text-sm">
            No blocks. Add one from the panel below.
          </div>
        )}
      </div>

      <AddBlockBar onAdd={onAddBlock} />
    </div>
  );
}

function AddBlockBar({ onAdd }) {
  const [type, setType] = useState('hero');
  return (
    <div className="rounded-2xl border orka-border-subtle orka-surface-base p-3 flex items-center gap-2" data-testid="owner-cms-add-block-bar">
      <span className="text-xs orka-text-tertiary uppercase tracking-wider font-semibold pl-1">Add block</span>
      <select
        value={type}
        onChange={e => setType(e.target.value)}
        className="h-9 px-3 rounded-lg border orka-border-default bg-transparent orka-text-primary text-sm"
        data-testid="owner-cms-add-block-type"
      >
        {BLOCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <button
        type="button"
        onClick={() => onAdd(type)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm"
        data-testid="owner-cms-add-block-submit"
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}

function BlockEditor({ block, index, total, onChange }) {
  const [draft, setDraft] = useState(() => JSON.stringify(block.data || {}, null, 2));
  const [orderDraft, setOrderDraft] = useState(block.order);
  const [typeDraft, setTypeDraft] = useState(block.block_type);
  const [saving, setSaving] = useState(false);
  const [parseError, setParseError] = useState(null);

  useEffect(() => {
    setDraft(JSON.stringify(block.data || {}, null, 2));
    setOrderDraft(block.order);
    setTypeDraft(block.block_type);
    setParseError(null);
  }, [block.block_id, block.updated_at]); // eslint-disable-line

  const dirty =
    draft !== JSON.stringify(block.data || {}, null, 2) ||
    orderDraft !== block.order ||
    typeDraft !== block.block_type;

  const onSave = async () => {
    let data;
    try { data = JSON.parse(draft); }
    catch (e) { setParseError(e.message); return; }
    setParseError(null);
    setSaving(true);
    try {
      await cmsOwner.updateBlock(block.block_id, {
        data,
        order: Number(orderDraft),
        block_type: typeDraft,
      });
      toast.success('Block saved');
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onMove = async (delta) => {
    const next = Math.max(0, Math.min(total - 1, block.order + delta));
    if (next === block.order) return;
    try {
      await cmsOwner.updateBlock(block.block_id, { order: next });
      onChange();
    } catch (e) { toast.error(e.message); }
  };

  const onDelete = async () => {
    // Confirm deletion explicitly
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this ${block.block_type} block?`)) return;
    try {
      await cmsOwner.deleteBlock(block.block_id);
      toast.success('Block deleted');
      onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="rounded-2xl border orka-border-subtle orka-surface-base p-4" data-testid={`owner-cms-block-${block.block_id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono orka-text-tertiary">#{index + 1}</span>
        <select
          value={typeDraft}
          onChange={e => setTypeDraft(e.target.value)}
          className="h-8 px-2 rounded-lg border orka-border-default bg-transparent orka-text-primary text-xs"
          data-testid={`owner-cms-block-${block.block_id}-type`}
        >
          {BLOCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          type="number"
          value={orderDraft}
          onChange={e => setOrderDraft(e.target.value)}
          className="w-16 h-8 px-2 rounded-lg border orka-border-default bg-transparent orka-text-primary text-xs"
          aria-label="Order"
          data-testid={`owner-cms-block-${block.block_id}-order`}
        />
        <button type="button" onClick={() => onMove(-1)} className="w-7 h-7 rounded-md inline-flex items-center justify-center orka-text-tertiary hover:orka-surface-subtle" title="Move up" data-testid={`owner-cms-block-${block.block_id}-up`}>
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onMove(1)} className="w-7 h-7 rounded-md inline-flex items-center justify-center orka-text-tertiary hover:orka-surface-subtle" title="Move down" data-testid={`owner-cms-block-${block.block_id}-down`}>
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
        <span className="ml-auto text-[11px] orka-text-tertiary font-mono">{block.block_id}</span>
        <button type="button" onClick={onDelete} className="w-7 h-7 rounded-md inline-flex items-center justify-center text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30" title="Delete" data-testid={`owner-cms-block-${block.block_id}-delete`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        rows={Math.min(20, Math.max(4, draft.split('\n').length))}
        spellCheck={false}
        className="w-full font-mono text-xs leading-relaxed p-3 rounded-lg border orka-border-subtle orka-surface-page orka-text-primary"
        data-testid={`owner-cms-block-${block.block_id}-data`}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        {parseError ? (
          <div className="text-xs text-rose-600 font-mono truncate" data-testid={`owner-cms-block-${block.block_id}-error`}>{parseError}</div>
        ) : <div />}
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs disabled:opacity-50"
          data-testid={`owner-cms-block-${block.block_id}-save`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {saving ? 'Saving' : (dirty ? 'Save' : 'Saved')}
        </button>
      </div>
    </div>
  );
}
